// modules/dashboard/pages/DashboardGeneral.js
import { useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import { getPersonas as getPersonasRBC } from "../../estrategias/rbc/api/RBCAPI";
import { getPersonas as getPersonasVejez } from "../../estrategias/vejez/api/VejezAPI";
import { getPersonas as getPersonasMigrante } from "../../estrategias/migrante/api/MigranteAPI";
import { getPersonas as getPersonasHabitanteCalle } from "../../estrategias/habitante_calle/api/HabitanteCalleAPI";
import {
  unificarTerritorio, calcularFormatoCompartido, calcularRBC, calcularComparativoComunidades,
} from "../utils/agregaciones";
import SeccionRBC from "../components/SeccionRBC";
import SeccionFormatoCompartido from "../components/SeccionFormatoCompartido";
import ComparativoComunidades from "../components/ComparativoComunidades";
import GraficoBarras from "../components/GraficoBarras";
import { Tarjeta, Grilla, num, pct } from "../components/Piezas";
import { ESTRATEGIA_INFO, ESTRATEGIA_COLOR, AZUL_INSTITUCIONAL } from "../utils/colores";
import "../styles/dashboard.css";

const NAV_LINKS = [
  { href: "#resumen", label: "Resumen" },
  ...ESTRATEGIA_INFO.map((e) => ({ href: `#${e.clave}`, label: e.corto, color: e.color })),
  { href: "#territorial", label: "Comunidades" },
  { href: "#hallazgos", label: "Hallazgos" },
];

// Títulos con mayúscula inicial: "PUERTO NARIÑO" -> "Puerto Nariño"
function tituloTerritorio(t) {
  return String(t).toLowerCase().replace(/(^|\s|-)(\p{L})/gu, (m, a, b) => a + b.toUpperCase());
}

function irA(id) {
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
}

export default function DashboardGeneral() {
  const [cargando, setCargando] = useState(true);
  const [errores, setErrores] = useState([]);
  const [rbcRaw, setRbcRaw] = useState([]);
  const [vejezRaw, setVejezRaw] = useState([]);
  const [migranteRaw, setMigranteRaw] = useState([]);
  const [habCalleRaw, setHabCalleRaw] = useState([]);

  const [territorioFiltro, setTerritorioFiltro] = useState("TODOS");
  const [comunidadFiltro, setComunidadFiltro] = useState("TODAS");
  const [actualizadoEn, setActualizadoEn] = useState(null);

  useEffect(() => {
    async function cargarTodo() {
      setCargando(true);
      const fallos = [];

      const [rRbc, rVejez, rMigrante, rHabCalle] = await Promise.allSettled([
        getPersonasRBC(), getPersonasVejez(), getPersonasMigrante(), getPersonasHabitanteCalle(),
      ]);

      if (rRbc.status === "fulfilled") setRbcRaw(rRbc.value.data); else fallos.push("RBC/Discapacidad");
      if (rVejez.status === "fulfilled") setVejezRaw(rVejez.value.data); else fallos.push("Vejez");
      if (rMigrante.status === "fulfilled") setMigranteRaw(rMigrante.value.data); else fallos.push("Población Migrante");
      if (rHabCalle.status === "fulfilled") setHabCalleRaw(rHabCalle.value.data); else fallos.push("Habitante de Calle");

      setErrores(fallos);
      setActualizadoEn(new Date());
      setCargando(false);

      if (fallos.length) {
        Swal.fire({
          icon: "warning",
          title: "Algunos datos no cargaron",
          text: `No se pudo traer información de: ${fallos.join(", ")}. El resto del tablero sigue funcionando con lo que sí cargó.`,
          confirmButtonColor: "#1a3a5c",
        });
      }
    }
    cargarTodo();
  }, []);

  // ── Territorios/comunidades disponibles (unificados), a partir de los datos reales ──
  const territorios = useMemo(() => {
    const todos = [rbcRaw, vejezRaw, migranteRaw, habCalleRaw].flat();
    return Array.from(new Set(todos.map((p) => unificarTerritorio(p.municipio)).filter(Boolean))).sort();
  }, [rbcRaw, vejezRaw, migranteRaw, habCalleRaw]);

  const comunidadesDisponibles = useMemo(() => {
    const todos = [rbcRaw, vejezRaw, migranteRaw, habCalleRaw].flat();
    const base = territorioFiltro === "TODOS" ? todos : todos.filter((p) => unificarTerritorio(p.municipio) === territorioFiltro);
    return Array.from(new Set(base.map((p) => p.nombre_comunidad).filter(Boolean).map((c) => String(c).trim()))).sort();
  }, [rbcRaw, vejezRaw, migranteRaw, habCalleRaw, territorioFiltro]);

  useEffect(() => { setComunidadFiltro("TODAS"); }, [territorioFiltro]);

  function filtrar(lista) {
    return lista.filter((p) => {
      const okTerritorio = territorioFiltro === "TODOS" || unificarTerritorio(p.municipio) === territorioFiltro;
      const okComunidad = comunidadFiltro === "TODAS" || String(p.nombre_comunidad || "").trim() === comunidadFiltro;
      return okTerritorio && okComunidad;
    });
  }

  const datosRBC = useMemo(() => calcularRBC(filtrar(rbcRaw)), [rbcRaw, territorioFiltro, comunidadFiltro]); // eslint-disable-line react-hooks/exhaustive-deps
  const datosVejez = useMemo(() => calcularFormatoCompartido(filtrar(vejezRaw)), [vejezRaw, territorioFiltro, comunidadFiltro]); // eslint-disable-line react-hooks/exhaustive-deps
  const datosMigrante = useMemo(() => calcularFormatoCompartido(filtrar(migranteRaw)), [migranteRaw, territorioFiltro, comunidadFiltro]); // eslint-disable-line react-hooks/exhaustive-deps
  const datosHabCalle = useMemo(() => calcularFormatoCompartido(filtrar(habCalleRaw)), [habCalleRaw, territorioFiltro, comunidadFiltro]); // eslint-disable-line react-hooks/exhaustive-deps

  const comparativo = useMemo(() => calcularComparativoComunidades({
    rbc: filtrar(rbcRaw), vejez: filtrar(vejezRaw), migrante: filtrar(migranteRaw), habitante_calle: filtrar(habCalleRaw),
  }), [rbcRaw, vejezRaw, migranteRaw, habCalleRaw, territorioFiltro, comunidadFiltro]); // eslint-disable-line react-hooks/exhaustive-deps

  const filtroActivo = territorioFiltro !== "TODOS" || comunidadFiltro !== "TODAS";
  const etiquetaFiltro = [
    territorioFiltro !== "TODOS" ? tituloTerritorio(territorioFiltro) : null,
    comunidadFiltro !== "TODAS" ? comunidadFiltro : null,
  ].filter(Boolean).join(" · ");

  // Totales por estrategia (con filtros aplicados) para la portada y el resumen.
  const totales = {
    rbc: datosRBC.total, vejez: datosVejez.total, migrante: datosMigrante.total, habitante_calle: datosHabCalle.total,
  };
  const totalGeneral = Object.values(totales).reduce((a, b) => a + b, 0);

  // Personas por territorio, sumando las 4 estrategias.
  const porTerritorio = useMemo(() => {
    const c = {};
    [rbcRaw, vejezRaw, migranteRaw, habCalleRaw].forEach((lista) =>
      filtrar(lista).forEach((p) => {
        const t = unificarTerritorio(p.municipio);
        if (t) c[tituloTerritorio(t)] = (c[tituloTerritorio(t)] || 0) + 1;
      })
    );
    return c;
  }, [rbcRaw, vejezRaw, migranteRaw, habCalleRaw, territorioFiltro, comunidadFiltro]); // eslint-disable-line react-hooks/exhaustive-deps

  // Hallazgos automáticos (frases cortas a partir de los datos actuales).
  const hallazgos = [];
  if (totalGeneral > 0) {
    const mayor = ESTRATEGIA_INFO.map((e) => ({ ...e, n: totales[e.clave] })).sort((a, b) => b.n - a.n)[0];
    hallazgos.push({ color: mayor.color, titulo: "Estrategia con más caracterización",
      texto: `${mayor.nombre} concentra ${num(mayor.n)} personas, el ${pct(mayor.n, totalGeneral)}% del total.` });
  }
  const topTerr = Object.entries(porTerritorio).sort((a, b) => b[1] - a[1])[0];
  if (topTerr) hallazgos.push({ color: AZUL_INSTITUCIONAL, titulo: "Territorio con más registros",
    texto: `${topTerr[0]} reúne ${num(topTerr[1])} personas (${pct(topTerr[1], totalGeneral)}%) de las 4 estrategias.` });
  if (datosRBC.total > 0) hallazgos.push({ color: ESTRATEGIA_COLOR.rbc, titulo: "Certificación de discapacidad",
    texto: `Solo el ${pct(datosRBC.certificado["Sí"] || 0, datosRBC.total)}% de las personas de RBC tiene certificado de discapacidad (RLCPCD) — una brecha para el acceso a beneficios.` });
  if (datosVejez.total > 0) hallazgos.push({ color: ESTRATEGIA_COLOR.vejez, titulo: "Salud de las personas mayores",
    texto: `El ${pct(datosVejez.enfermedadesCronicas["Sí"] || 0, datosVejez.total)}% reporta enfermedad crónica y el ${pct(datosVejez.vacunacion["Sí"] || 0, datosVejez.total)}% tiene la vacunación al día.` });
  hallazgos.push({ color: ESTRATEGIA_COLOR.migrante, titulo: "Réplica entre estrategias activa",
    texto: "Las personas que pertenecen a más de un grupo poblacional (ej. persona mayor y migrante) quedan registradas automáticamente en cada estrategia que les corresponde." });

  if (cargando) {
    return (
      <div className="db py-5 text-center" style={{ color: "#5b6575" }}>
        <div className="spinner-border" role="status" style={{ color: AZUL_INSTITUCIONAL }} />
        <div className="mt-3">Cargando datos de las 4 estrategias…</div>
      </div>
    );
  }

  return (
    <div className="db pb-2">
      {/* ── Portada ── */}
      <header className="db-portada">
        <div className="db-kicker">Secretaría de Salud del Amazonas · Promoción Social</div>
        <h1>Promoción Social en cifras</h1>
        <p>
          Lectura en vivo de las cuatro estrategias de Promoción Social del departamento del Amazonas.
          Usa los filtros de territorio y comunidad para acotar todo el tablero.
        </p>

        <div className="db-cifras">
          <div className="db-cifra total">
            <div className="db-cifra-num">{num(totalGeneral)}</div>
            <div className="db-cifra-etq">Personas caracterizadas{filtroActivo ? " (con filtro)" : ""}</div>
          </div>
          {ESTRATEGIA_INFO.map((e) => (
            <button type="button" key={e.clave} className="db-cifra" onClick={() => irA(e.clave)} title={`Ir a ${e.nombre}`}>
              <div className="db-cifra-num">{num(totales[e.clave])}</div>
              <div className="db-cifra-etq"><span className="db-punto" style={{ background: e.color }} />{e.corto}</div>
              <div className="db-cifra-pct">{pct(totales[e.clave], totalGeneral)}% del total</div>
            </button>
          ))}
        </div>

        <div className="db-portada-pie">
          {actualizadoEn && <span>🕒 Actualizado {actualizadoEn.toLocaleString("es-CO", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</span>}
          <span className="db-no-imprimir" style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
            <button type="button" className="db-btn-claro" onClick={() => window.print()}>🖨️ Imprimir / PDF</button>
          </span>
        </div>
      </header>

      {/* ── Navegación + filtros ── */}
      <div className="db-barra" role="navigation" aria-label="Secciones y filtros del dashboard">
        <nav className="db-nav">
          {NAV_LINKS.map((l) => (
            <a key={l.href} href={l.href} onClick={(ev) => { ev.preventDefault(); irA(l.href.slice(1)); }}>
              {l.color && <i style={{ width: 8, height: 8, borderRadius: "50%", background: l.color, display: "inline-block" }} />}
              {l.label}
            </a>
          ))}
        </nav>
        <div className="db-filtros">
          <select className="form-select" value={territorioFiltro} onChange={(e) => setTerritorioFiltro(e.target.value)} aria-label="Filtrar por territorio">
            <option value="TODOS">Todos los territorios</option>
            {territorios.map((t) => <option key={t} value={t}>{tituloTerritorio(t)}</option>)}
          </select>
          <select className="form-select" value={comunidadFiltro} onChange={(e) => setComunidadFiltro(e.target.value)} aria-label="Filtrar por comunidad">
            <option value="TODAS">Todas las comunidades</option>
            {comunidadesDisponibles.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          {filtroActivo && (
            <button type="button" className="db-limpiar" onClick={() => { setTerritorioFiltro("TODOS"); setComunidadFiltro("TODAS"); }}>
              ✕ Limpiar
            </button>
          )}
        </div>
      </div>

      {filtroActivo && <div className="db-filtro-activo">🔎 Filtro activo: {etiquetaFiltro}</div>}

      {errores.length > 0 && (
        <div className="alert alert-warning py-2 px-3 mt-3 mb-0" style={{ borderRadius: 12, fontSize: "0.85rem" }}>
          ⚠️ No se pudo cargar: {errores.join(", ")}. El resto del tablero funciona con lo que sí llegó.
        </div>
      )}

      {/* ── Resumen general ── */}
      <section id="resumen" className="db-seccion" aria-labelledby="resumen-titulo">
        <div className="db-seccion-cab" style={{ "--acento": AZUL_INSTITUCIONAL }}>
          <div className="db-seccion-icono" aria-hidden="true">📊</div>
          <h2 id="resumen-titulo">Resumen general</h2>
        </div>
        <p className="db-seccion-desc">Cómo se reparten las personas caracterizadas entre estrategias y territorios.</p>
        <Grilla>
          <Tarjeta titulo="Distribución por estrategia" ancho={5}>
            {totalGeneral === 0 ? <div className="text-muted" style={{ fontSize: 13 }}>Sin datos</div> : (
              <>
                <div className="db-prop-barra" style={{ height: 18 }} role="img"
                  aria-label={ESTRATEGIA_INFO.map((e) => `${e.corto}: ${totales[e.clave]}`).join(", ")}>
                  {ESTRATEGIA_INFO.filter((e) => totales[e.clave] > 0).map((e) => (
                    <div key={e.clave} title={`${e.nombre}: ${totales[e.clave]} (${pct(totales[e.clave], totalGeneral)}%)`}
                      style={{ width: `${(totales[e.clave] / totalGeneral) * 100}%`, background: e.color }} />
                  ))}
                </div>
                <div style={{ display: "grid", gap: 10, marginTop: 14 }}>
                  {ESTRATEGIA_INFO.map((e) => (
                    <div key={e.clave} style={{ display: "flex", alignItems: "center", gap: 10, fontSize: "0.86rem" }}>
                      <i style={{ width: 12, height: 12, borderRadius: 3, background: e.color, flexShrink: 0 }} />
                      <span style={{ flex: 1, color: "#334155" }}>{e.emoji} {e.nombre}</span>
                      <strong style={{ fontVariantNumeric: "tabular-nums" }}>{num(totales[e.clave])}</strong>
                      <span style={{ width: 52, textAlign: "right", color: "#8a94a3", fontSize: "0.78rem" }}>{pct(totales[e.clave], totalGeneral)}%</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </Tarjeta>
          <Tarjeta titulo="Personas por territorio" nota="Suma de las 4 estrategias" ancho={7}>
            <GraficoBarras datos={porTerritorio} color={AZUL_INSTITUCIONAL} />
          </Tarjeta>
        </Grilla>
      </section>

      <SeccionRBC datos={datosRBC} />
      <SeccionFormatoCompartido id="vejez" titulo="Vejez" emoji="🧓" accent={ESTRATEGIA_COLOR.vejez} datos={datosVejez} etiquetaExcluir="Persona mayor"
        descripcion="Atención integral a personas mayores — Formato de Caracterización Población de Alto Valor Constitucional." />
      <SeccionFormatoCompartido id="habitante_calle" titulo="Habitante de Calle" emoji="🏙️" accent={ESTRATEGIA_COLOR.habitante_calle} datos={datosHabCalle} etiquetaExcluir="Habitante de calle"
        descripcion="Atención a población en situación de calle." />
      <SeccionFormatoCompartido id="migrante" titulo="Población Migrante" emoji="🧭" accent={ESTRATEGIA_COLOR.migrante} datos={datosMigrante} etiquetaExcluir="Migrante"
        descripcion="Atención a población migrante y su condición migratoria." />

      <section id="territorial" className="db-seccion" aria-labelledby="territorial-titulo">
        <div className="db-seccion-cab" style={{ "--acento": AZUL_INSTITUCIONAL }}>
          <div className="db-seccion-icono" aria-hidden="true">📍</div>
          <h2 id="territorial-titulo">Comparativo por comunidad</h2>
          <span className="db-seccion-n">{comparativo.length} comunidades</span>
        </div>
        <p className="db-seccion-desc">Volumen de caracterización por comunidad en las 4 estrategias.</p>
        <ComparativoComunidades filas={comparativo} />
      </section>

      <section id="hallazgos" className="db-seccion" aria-labelledby="hallazgos-titulo">
        <div className="db-seccion-cab" style={{ "--acento": AZUL_INSTITUCIONAL }}>
          <div className="db-seccion-icono" aria-hidden="true">📌</div>
          <h2 id="hallazgos-titulo">Hallazgos clave</h2>
        </div>
        <p className="db-seccion-desc">Lectura automática de los datos actuales{filtroActivo ? ` (${etiquetaFiltro})` : ""}.</p>
        <div className="db-hallazgos">
          {hallazgos.map((h) => (
            <div key={h.titulo} className="db-hallazgo" style={{ "--acento": h.color }}>
              <strong>{h.titulo}</strong>{h.texto}
            </div>
          ))}
        </div>
      </section>

      <div className="db-fuente">
        Fuente: Sistema Integrado de Promoción Social · Secretaría de Salud del Amazonas · datos en vivo de las 4 estrategias.
      </div>
    </div>
  );
}
